import { SxProps, Theme } from '@mui/material';
import { useCallback, useEffect } from 'react';

import { useGeolocation } from '@ocx-app/lib/geolocation/useGeolocation';

import { LocationSelector } from './location-selector.component';
import { useLocalOffersLocationContext } from '../context';
import { useLocationSelectorModal } from '../location-selector-modal';

export type LocationSelectorControllerProps = {
  onLocationClick?: () => void;
  sx?: SxProps<Theme>;
};

export const LocationSelectorController = ({ onLocationClick, sx }: LocationSelectorControllerProps) => {
  const { open: openLocationSelectorModal } = useLocationSelectorModal();
  const { locationState, customLocation, updateGeolocationData } = useLocalOffersLocationContext();

  // Use geolocation hook and pass data to context
  const { coordinates, status, loading, refresh } = useGeolocation();

  // Bridge geolocation data from this component to the context provider
  useEffect(() => {
    // We call useGeolocation here instead of in the LocalOffersLocationProvider to avoid
    // automatic permission requests on app initialization. The useGeolocation hook has
    // implementation details that cause it to request permissions even when configured
    // not to, which would inappropriately prompt users for location access on pages
    // like login where location functionality isn't needed. By placing the hook here,
    // permissions are only requested when users actually interact with location features.
    updateGeolocationData({
      coordinates,
      permissionStatus: status,
      refreshState: refresh,
      loading,
    });
  }, [coordinates, status, loading, updateGeolocationData, refresh]);

  const handleLocationClick = useCallback(() => {
    if (onLocationClick) {
      onLocationClick();
      return;
    }

    openLocationSelectorModal();
  }, [onLocationClick, openLocationSelectorModal]);

  return (
    <LocationSelector
      locationState={locationState}
      customLocationName={customLocation?.name}
      onClick={handleLocationClick}
      sx={sx}
    />
  );
};
